// page-meeting-guides.ts — Guides de réunion, dans le bloc Informations.
//
// Tout tient désormais dans le bandeau d'en-tête de la fiche réunion ; l'onglet
// dédié a été supprimé (août 2026).
//
//   - 3e colonne : la liste des guides, groupée par phase, aux teintes du
//     déroulé « Préparez, animez, concluez » (variables `--phase-*`). C'est LÀ
//     qu'un animateur déclare, d'un clic sur le picto d'état.
//   - 1re colonne, sous les champs d'identification : l'ordre du jour et les
//     objectifs, en aperçu (les premiers mots, le texte entier au survol). La
//     saisie se fait dans une popup, pour ne pas rendre le bandeau démesuré.
//
// Deux régimes distincts :
// - les GUIDES se déclarent réservés aux animateurs, et restent déclarables
//   même réunion fermée (les guides de conclusion sont post-réunion par
//   nature) ;
// - l'ORDRE DU JOUR et les OBJECTIFS sont des champs d'entête, gouvernés
//   exactement comme le libellé : lecture seule pour qui ne peut pas modifier
//   le bloc Informations, et pas de verrou sur la fermeture.
//
// `esc` et `t` sont des globales (cf. types/core-globals.d.ts).

import {
  groupByPhase,
  isGuideOverdue,
  nextStatus,
  summarize,
  type GuideItem,
  type GuidePhase,
  type GuideStatus,
} from '../domain/meeting-guides.js';
import {
  buildActionPrefill,
  hasStandardAction,
  type MeetingContext,
} from '../domain/meeting-guide-action.js';
import { plainTextFromRichText, sanitizeRichText } from '../utils/rich-text.js';

export interface GuidesPayload {
  guides: GuideItem[];
  agenda: string | null;
  objectives: string | null;
  duration_minutes: number | null;
  duration_warning: boolean;
  /** Date de la réunion (YYYY-MM-DD) : origine des échéances de guide. */
  meeting_date: string | null;
  /** État « À faire » résolu par le serveur, pour une action créée d'ici. */
  default_action_status_id: number | null;
}

interface PreviousAgenda {
  agenda: string | null;
  objectives: string | null;
  source: { id: number; label: string; meeting_date: string | null } | null;
}

type AgendaField = 'agenda' | 'objectives';

const SUMMARY_HOST_ID = 'mp-guides-summary';
const AGENDA_HOST_ID = 'mp-agenda-fields';

// ── Guides (3e colonne) ──────────────────────────────────────────────────────

const STATUS_ICON: Record<GuideStatus, string> = {
  pending: '○',
  done: '✔',
  skipped: '✖',
};

/** Couleur du picto d'état. Variables de thème uniquement. */
const STATUS_COLOR: Record<GuideStatus, string> = {
  pending: 'var(--text3)',
  done: 'var(--accent)',
  skipped: 'var(--danger)',
};

function phaseLabel(phase: GuidePhase): string {
  return t('pageMeeting.guides.phase.' + phase);
}

/** Traçabilité en texte brut : qui a statué et quand, ou l'origine système.
 * Chaîne vide tant que personne n'a statué. */
function traceText(g: GuideItem): string {
  if (g.status === 'pending') return '';
  if (g.prepositioned) return t('pageMeeting.guides.prepositioned');
  const who = g.checked_by_name || '';
  // `fmtDateTimeLocal` (core.ts) : ajoute le suffixe UTC manquant et rend en
  // JJ/MM/AAAA HH:MM. Un `toLocaleString()` nu sortirait au format américain
  // sur un poste configuré en anglais.
  const when = g.checked_at ? fmtDateTimeLocal(g.checked_at) : '';
  if (!who && !when) return '';
  return t('pageMeeting.guides.declaredBy').replace('{who}', who).replace('{when}', when);
}

/** Point de montage de la 3e colonne. Rempli par `wireGuidesHeader`. */
export function renderGuidesSummaryMount(): string {
  return `<div id="${SUMMARY_HOST_ID}"></div>`;
}

/**
 * Compteur d'en-tête : un décompte PAR ÉTAT, pas un ratio.
 *
 * Un « 3/6 » mettait exécutés et ignorés dans le même sac alors qu'ils ne
 * disent pas la même chose du déroulé d'une réunion. Les trois pictos sont ceux
 * de la liste, avec les mêmes couleurs : la lecture est immédiate.
 */
function renderCounter(guides: GuideItem[]): string {
  const s = summarize(guides);
  const chip = (status: GuideStatus, n: number): string =>
    `<span title="${esc(t('pageMeeting.guides.status.' + status))}"
        style="display:inline-flex;align-items:center;gap:3px;color:${STATUS_COLOR[status]}">
        <span aria-hidden="true">${STATUS_ICON[status]}</span>${n}</span>`;
  return `<span style="display:inline-flex;gap:8px;font-size:12px;font-weight:500">
      ${chip('done', s.done)}${chip('skipped', s.skipped)}${chip('pending', s.pending)}
    </span>`;
}

/**
 * CTA de création de l'action standard d'un guide.
 *
 * Trois cas, et seul le premier rend un bouton :
 * - action proposée et pas encore créée → « + » ;
 * - action déjà créée (par n'importe quel animateur) → rien, mais le libellé
 *   du guide porte l'explication en infobulle, sinon la disparition du
 *   bouton serait incompréhensible pour le suivant ;
 * - pas d'action standard sur ce guide → rien du tout.
 */
function renderActionCta(g: GuideItem, canCreate: boolean): string {
  if (!hasStandardAction(g)) return '';
  if (g.action_created) {
    // Marque visible, et pas seulement l'absence du bouton : le suivant doit
    // voir que l'action existe, sans avoir à survoler quoi que ce soit. Picto
    // distinct des trois états du guide (○ ✔ ✖), qui disent autre chose.
    const done = t('pageMeeting.guides.actionDone');
    return `<span class="mpgs-action-done" title="${esc(done)}" aria-label="${esc(done)}"
        style="flex:none;color:var(--accent);line-height:1.55">⚑</span>`;
  }
  if (!canCreate) return '';
  const hint = t('pageMeeting.guides.createAction').replace('{label}', g.action_label);
  return `<button type="button" class="mpgs-action" data-guide-id="${g.id}"
      title="${esc(hint)}" aria-label="${esc(hint)}"
      style="flex:none;background:none;border:none;padding:0 2px;cursor:pointer;color:var(--accent);font:inherit;line-height:1.55">+</button>`;
}

/**
 * Rendu pur du récapitulatif. Exporté pour être testé sans DOM.
 *
 * `canDeclare` : un animateur déclare directement ici (l'icône devient un
 * bouton qui fait tourner l'état) ; les autres n'ont qu'une vue.
 * `canCreateAction` : droit d'écriture sur les actions — sans lui, le CTA de
 * création est masqué plutôt que de mener à un 403 à l'enregistrement.
 */
/** Date du jour telle que la voit l'utilisateur (fuseau du navigateur).
 * `toISOString()` donnerait la date UTC — un soir d'été à Paris, il annoncerait
 * déjà le lendemain. */
function todayLocal(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Picto de retard, après le libellé : l'échéance du guide est passée et
 * personne n'a statué. Orange (avertissement), pas rouge : le guide est un
 * conseil de conduite de réunion, pas une faute. */
function overdueBadge(g: GuideItem, meetingDate: string | null, today: string): string {
  if (!isGuideOverdue(g, meetingDate, today)) return '';
  return `<span title="${esc(t('pageMeeting.guides.overdue'))}" aria-label="${esc(
    t('pageMeeting.guides.overdue'),
  )}" style="flex:none;color:var(--warn);margin-left:4px">⚠</span>`;
}

export function renderGuidesSummaryCard(
  guides: GuideItem[],
  canDeclare = false,
  canCreateAction = false,
  meetingDate: string | null = null,
  today: string = todayLocal(),
): string {
  if (!guides.length) return '';
  const bubbles = groupByPhase(guides)
    .map((group) => {
      const rows = group.items
        .map((g) => {
          // Deux infobulles distinctes : l'icône explique l'ÉTAT, le libellé
          // court donne le libellé long et la traçabilité.
          const detail = [g.description, traceText(g)].filter(Boolean).join(' — ');
          const state = t('pageMeeting.guides.status.' + g.status);
          const iconStyle = `flex:none;width:16px;text-align:center;line-height:1.55;color:${STATUS_COLOR[g.status]}`;
          const icon = canDeclare
            ? `<button type="button" class="mpgs-toggle" data-guide-id="${g.id}"
                title="${esc(state + ' — ' + t('pageMeeting.guides.toggleHint'))}"
                aria-label="${esc(state)}"
                style="${iconStyle};background:none;border:none;padding:0;cursor:pointer;font:inherit">${STATUS_ICON[g.status]}</button>`
            : `<span title="${esc(state)}" aria-label="${esc(state)}" style="${iconStyle}">${STATUS_ICON[g.status]}</span>`;
          // Pas de troncature : la colonne est étroite, le libellé passe à la
          // ligne. Tronquer imposerait le survol pour lire un nom de guide,
          // alors que l'infobulle est là pour le DÉTAIL, pas pour l'essentiel.
          // L'infobulle du libellé porte aussi la raison d'absence du CTA :
          // sans elle, un second animateur ne comprendrait pas pourquoi le
          // bouton a disparu.
          const tip = [detail, g.action_created ? t('pageMeeting.guides.actionDone') : '']
            .filter(Boolean)
            .join(' — ');
          return `<div style="display:flex;align-items:flex-start;gap:6px;min-width:0">
              ${icon}
              <span title="${esc(tip)}" style="min-width:0;flex:1">${esc(g.name)}${overdueBadge(g, meetingDate, today)}</span>
              ${renderActionCta(g, canCreateAction)}
            </div>`;
        })
        .join('');
      return `<div style="background:var(--phase-${group.phase}-bg);border-left:3px solid var(--phase-${group.phase});border-radius:8px;padding:8px 10px;display:flex;flex-direction:column;gap:6px">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.04em;color:var(--text3)">${esc(
            phaseLabel(group.phase),
          )}</div>
          ${rows}
        </div>`;
    })
    .join('');

  return `<div class="card" style="margin-top:0;background:var(--bg2);font-size:13px;line-height:1.55">
      <h2 style="font-size:14px;margin:0 0 10px 0;color:var(--text);display:flex;align-items:baseline;justify-content:space-between;gap:8px">
        <span>${esc(t('pageMeeting.tab.guides'))}</span>
        ${renderCounter(guides)}
      </h2>
      <div style="display:flex;flex-direction:column;gap:8px">${bubbles}</div>
    </div>`;
}

// ── Ordre du jour et objectifs (1re colonne + popup) ─────────────────────────

/** Point de montage des aperçus, sous les champs du bloc Informations. */
export function renderAgendaFieldsMount(): string {
  return `<div id="${AGENDA_HOST_ID}"></div>`;
}

const FIELD_LABEL: Record<AgendaField, string> = {
  agenda: 'pageMeeting.guides.agenda',
  objectives: 'pageMeeting.guides.objectives',
};

/**
 * Aperçus cliquables, rendu pur.
 *
 * Le contenu est rendu en TEXTE RICHE, coupé à deux lignes par la CSS (cf.
 * `.mpg-preview` dans app.css) : une troncature au caractère aurait aplati les
 * puces et les paragraphes d'un ordre du jour en un pavé illisible. L'infobulle
 * reprend le texte entier avec ses retours à la ligne — c'est la seule mise en
 * forme qu'un `title` natif sache rendre.
 *
 * L'aperçu est cliquable, et un crayon le double : le cadre ne se signale pas
 * comme éditable à l'œil, le crayon est la convention maison pour ça.
 *
 * `<div role="button">` et non `<button>` : le contenu riche porte des blocs
 * (`<p>`, `<ul>`), interdits dans un bouton.
 */
export function renderAgendaFields(payload: GuidesPayload, readOnly: boolean): string {
  const row = (key: AgendaField): string => {
    const full = plainTextFromRichText(payload[key], '\n');
    const rich = sanitizeRichText(payload[key]);
    const hint = readOnly ? t('pageMeeting.guides.agendaReadOnly') : t('common.edit');
    const body =
      rich || `<span style="color:var(--text3)">${esc(t('pageMeeting.guides.agendaEmpty'))}</span>`;
    return `<div class="form-group" style="flex:1 1 240px">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
          <label style="margin:0">${esc(t(FIELD_LABEL[key]))}</label>
          <button type="button" class="btn btn-secondary btn-icon mpg-open-agenda"
            data-field="${key}" title="${esc(hint)}"
            aria-label="${esc(t(FIELD_LABEL[key]) + ' — ' + hint)}">✎</button>
        </div>
        <div class="mpg-preview mpg-open-agenda" data-field="${key}" role="button" tabindex="0"
          title="${esc(full || hint)}" aria-label="${esc(t(FIELD_LABEL[key]) + ' — ' + hint)}"
          ><div class="mpg-preview-body">${body}</div></div>
      </div>`;
  };
  return `<div class="form-row mt-3" style="align-items:flex-start">${row('agenda')}${row('objectives')}</div>`;
}

/**
 * Corps de la popup de saisie d'UN champ. Rendu pur, exporté pour être testé
 * sans DOM.
 *
 * Une popup par champ : l'ordre du jour et les objectifs se rédigent à des
 * moments différents, et une popup à deux éditeurs obligeait à en relire un
 * pour modifier l'autre.
 */
export function renderAgendaModalBody(
  field: AgendaField,
  value: string | null,
  readOnly: boolean,
): string {
  const reuse = readOnly
    ? ''
    : `<button type="button" class="btn btn-secondary mpg-reuse" data-field="${field}">${esc(
        t('pageMeeting.guides.reusePrevious'),
      )}</button>`;
  return `<div class="mpg-editor" id="mpg-editor-${field}" data-field="${field}"
      contenteditable="${readOnly ? 'false' : 'true'}" role="textbox" aria-multiline="true"
      aria-label="${esc(t(FIELD_LABEL[field]))}"
      style="min-height:200px;border:1px solid var(--border2);border-radius:var(--radius);padding:8px 10px;font-size:var(--fs-sm);line-height:1.6;background:var(--bg2);overflow:auto">${sanitizeRichText(
        value,
      )}</div>
    <div style="display:flex;align-items:center;gap:12px;margin-top:10px">
      ${reuse}
      <span id="mpg-agenda-feedback" class="text-sm text-muted"></span>
    </div>`;
}

// ── Câblage ──────────────────────────────────────────────────────────────────

/**
 * Charge les guides une fois et peint les deux surfaces du bandeau :
 * la liste (3e colonne) et les aperçus ordre du jour / objectifs (1re colonne).
 *
 * Silencieux en cas d'échec de chargement : les zones disparaissent plutôt que
 * d'afficher une erreur dans un bandeau d'en-tête. Un échec d'ENREGISTREMENT ou
 * de DÉCLARATION, lui, remonte en toast — l'utilisateur vient d'agir.
 */
/** Clés de `sessionStorage` partagées avec la page action (cf. page-action.ts). */
const ACTION_PREFILL_KEY = 'actionEditor:duplicatePrefill';
const ACTION_BACK_KEY = 'actionEditor:backHash';

export function wireGuidesHeader(
  meetingId: number,
  canDeclare: boolean,
  agendaReadOnly: boolean,
  actionContext: MeetingContext | null = null,
): void {
  const summaryHost = document.getElementById(SUMMARY_HOST_ID);
  const agendaHost = document.getElementById(AGENDA_HOST_ID);
  if (!summaryHost && !agendaHost) return;

  let payload: GuidesPayload | null = null;

  const feedback = (msgKey: string): void => {
    const el = document.getElementById('mpg-agenda-feedback');
    if (el) el.textContent = t(msgKey);
  };

  const paintSummary = (): void => {
    if (!summaryHost || !payload) return;
    summaryHost.innerHTML = renderGuidesSummaryCard(
      payload.guides,
      canDeclare,
      actionContext !== null,
      payload.meeting_date,
    );
    summaryHost.querySelectorAll<HTMLButtonElement>('.mpgs-action').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset['guideId']);
        const item = payload?.guides.find((g) => g.id === id);
        if (!item || !actionContext || !hasStandardAction(item)) return;
        // Rien n'est écrit ici : on pose le pré-remplissage et on ouvre la page
        // de création d'action habituelle. Annuler ne laisse aucune trace, et
        // le retour se fait sur la réunion (clé `backHash`, déjà en place).
        try {
          sessionStorage.setItem(
            ACTION_PREFILL_KEY,
            JSON.stringify(
              buildActionPrefill(
                item,
                actionContext,
                meetingId,
                payload?.default_action_status_id ?? null,
              ),
            ),
          );
          sessionStorage.setItem(ACTION_BACK_KEY, window.location.hash);
        } catch {
          /* sessionStorage indisponible : la page s'ouvrira vierge */
        }
        window.location.hash = '#action/0';
      });
    });
    summaryHost.querySelectorAll<HTMLButtonElement>('.mpgs-toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = Number(btn.dataset['guideId']);
        const item = payload?.guides.find((g) => g.id === id);
        if (!item || !payload) return;
        void (async () => {
          try {
            const updated = (await api.setMeetingGuideStatus(meetingId, id, {
              status: nextStatus(item.status),
            })) as GuideItem;
            if (payload) {
              payload.guides = payload.guides.map((g) => (g.id === id ? updated : g));
            }
            paintSummary();
          } catch (err: unknown) {
            toast(err instanceof Error ? err.message : t('common.error'), 'error');
          }
        })();
      });
    });
  };

  const readEditor = (field: AgendaField): string =>
    sanitizeRichText(
      document.querySelector<HTMLElement>(`.mpg-editor[data-field="${field}"]`)?.innerHTML || '',
    );

  const wireReuse = (field: AgendaField): void => {
    document.querySelector<HTMLButtonElement>('.mpg-reuse')?.addEventListener('click', () => {
      void (async () => {
        try {
          const prev = (await api.getPreviousAgenda(meetingId)) as PreviousAgenda;
          if (!prev.source) {
            feedback('pageMeeting.guides.noPrevious');
            return;
          }
          const editor = document.querySelector<HTMLElement>(`.mpg-editor[data-field="${field}"]`);
          if (editor) editor.innerHTML = sanitizeRichText(prev[field]);
          feedback('pageMeeting.guides.reused');
        } catch {
          feedback('common.error');
        }
      })();
    });
  };

  const openAgendaModal = (field: AgendaField): void => {
    if (!payload) return;
    const readOnly = agendaReadOnly;
    openWideModal(
      t(FIELD_LABEL[field]),
      renderAgendaModalBody(field, payload[field], readOnly),
      async () => {
        if (readOnly) {
          closeModal();
          return;
        }
        try {
          // Le PUT ne porte que le champ édité : `model_fields_set` côté
          // serveur laisse l'autre intact, on ne réécrit pas ce qu'on n'a pas
          // ouvert.
          const saved = (await api.setMeetingAgenda(meetingId, {
            [field]: readEditor(field),
          })) as { agenda: string | null; objectives: string | null };
          if (payload) {
            payload.agenda = saved.agenda;
            payload.objectives = saved.objectives;
          }
          closeModal();
          toast(t('common.saved'));
          paintAgenda();
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      },
      readOnly ? t('common.close') : t('common.save'),
      // En consultation, « Fermer » suffit : proposer « Annuler » à côté
      // laisserait croire qu'il y a quelque chose à annuler.
      readOnly,
    );
    wireReuse(field);
  };

  function paintAgenda(): void {
    if (!agendaHost || !payload) return;
    agendaHost.innerHTML = renderAgendaFields(payload, agendaReadOnly);
    agendaHost.querySelectorAll<HTMLElement>('.mpg-open-agenda').forEach((el) => {
      const open = (): void => openAgendaModal(el.dataset['field'] as AgendaField);
      el.addEventListener('click', open);
      // Le crayon est un vrai `<button>` : il gère déjà le clavier. L'aperçu,
      // lui, n'a que `role="button"` — sans ça il serait tabulable mais inerte.
      if (el.tagName !== 'BUTTON') {
        el.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            open();
          }
        });
      }
    });
  }

  void (async () => {
    try {
      payload = (await api.getMeetingGuides(meetingId)) as GuidesPayload;
      paintSummary();
      paintAgenda();
    } catch {
      if (summaryHost) summaryHost.innerHTML = '';
      if (agendaHost) agendaHost.innerHTML = '';
    }
  })();
}
